<template>
  <AdminLayout>
    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center min-h-screen">
      <div class="animate-spin rounded-full h-32 w-32 border-b-2 border-mifo-accent"></div>
    </div>

    <!-- Order content -->
    <div v-else-if="order" class="space-y-6">
      <!-- Breadcrumb -->
      <div class="mb-6">
        <nav class="flex" aria-label="Breadcrumb">
          <ol class="flex items-center space-x-2">
            <li v-if="order?.course_id">
              <router-link 
                :to="`/herzbeben/courses/${order.course_id}`"
                class="text-sm text-gray-500 hover:text-mifo-accent transition-colors"
              >
                ← Zurück zum Kurs
              </router-link>
            </li>
            <li v-else-if="order?.productId || order?.product_id">
              <router-link 
                :to="`/herzbeben/products/${order.productId || order.product_id}`"
                class="text-sm text-gray-500 hover:text-mifo-accent transition-colors"
              >
                ← Zurück zum Produkt
              </router-link>
            </li>
          </ol>
        </nav>
      </div>

      <!-- Header -->
      <div class="flex justify-between items-start">
        <div>
          <h1 class="text-2xl font-heading font-bold text-mifo-primary">
            Bestellung {{ order.id }}
          </h1>
          <p class="mt-1 text-sm text-gray-500">
            {{ formatDate(order.created_at) }} • {{ formatCurrency(order.total_amount) }}
          </p>
        </div>
        <div class="flex space-x-3">
          <button @click="showEditModal = true" class="btn-outline whitespace-nowrap">
            <Pencil class="h-4 w-4 mr-2 inline-block align-middle" />
            Bearbeiten
          </button>
          <button @click="showStatusModal = true" class="btn-outline whitespace-nowrap">
            <Settings class="h-4 w-4 mr-2 inline-block align-middle" />
            Status ändern
          </button>
          <button
            v-if="order.status === 'paid'"
            @click="openRefundDialog"
            class="btn-secondary whitespace-nowrap"
          >
            <RotateCcw class="h-4 w-4 mr-2 inline-block align-middle" />
            Rückerstatten
          </button>
        </div>
      </div>

      <!-- Order Info -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Customer Details -->
        <div class="card">
          <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">
            Kundendetails
          </h3>
          <div class="space-y-3">
            <div>
              <span class="text-sm font-medium text-gray-500">Name:</span>
              <p class="text-sm text-gray-900">
                {{ getFormattedCustomerName(order.customer) }}
              </p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">E-Mail:</span>
              <p class="text-sm text-gray-900">{{ order.customer.email }}</p>
            </div>
            <div v-if="order.customer.phone">
              <span class="text-sm font-medium text-gray-500">Telefon:</span>
              <p class="text-sm text-gray-900">{{ order.customer.phone }}</p>
            </div>
            <div v-if="order.customer.street">
              <span class="text-sm font-medium text-gray-500">Adresse:</span>
              <p class="text-sm text-gray-900">
                {{ order.customer.street }} {{ order.customer.houseNumber }}<br>
                {{ order.customer.postalCode }} {{ order.customer.city }}<br>
                {{ getCountryName(order.customer.country) }}
              </p>
            </div>
            <div v-if="order.customer.fachrichtung">
              <span class="text-sm font-medium text-gray-500">Fachrichtung:</span>
              <p class="text-sm text-gray-900">{{ order.customer.fachrichtung }}</p>
            </div>
            <div v-if="order.customer.schaller">
              <span class="text-sm font-medium text-gray-500">Schaller:</span>
              <p class="text-sm text-gray-900">{{ order.customer.schaller }}</p>
            </div>
          </div>
        </div>

        <!-- Order Details -->
        <div class="card">
          <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">
            Bestelldetails
          </h3>
          <div class="space-y-3">
            <div>
              <span class="text-sm font-medium text-gray-500">Status:</span>
              <div class="flex items-center space-x-2 ml-2">
                <StatusPill :value="order.status" />
                <div v-if="order.status === 'open' && timeUntilExpiration" class="flex items-center space-x-1">
                  <Clock class="h-3 w-3 text-orange-600" />
                  <span class="text-xs font-medium text-orange-600">
                    {{ formattedTimeLeft }}
                  </span>
                </div>
              </div>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Zahlungsmethode:</span>
              <p class="text-sm text-gray-900">{{ getPaymentMethodName(order.payment_method) }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Preiskategorie:</span>
              <p class="text-sm text-gray-900">{{ getCourseTypeName(order.course_type) }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Betrag:</span>
              <p class="text-lg font-heading font-bold text-mifo-primary">
                {{ formatCurrency(order.total_amount) }}
                <span v-if="refundSummary.refundedAmount > 0" class="text-sm font-normal text-red-700 ml-2">
                  (− {{ formatCurrency(refundSummary.refundedAmount) }})
                </span>
              </p>
            </div>
            <div>
              <span class="text-sm font-medium text-gray-500">Bestelldatum:</span>
              <p class="text-sm text-gray-900">{{ formatDate(order.created_at) }}</p>
            </div>
            <div v-if="order.molliePaymentId">
              <span class="text-sm font-medium text-gray-500">Mollie Payment ID:</span>
              <p class="text-sm text-gray-900 font-mono">{{ order.molliePaymentId }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Group Summary (when part of a grouped checkout) -->
      <div class="card" v-if="groupSummary && groupSummary.items && groupSummary.items.length > 1">
        <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">Gesamtübersicht (Warenkorb)</h3>
        <div class="space-y-2 text-sm">
          <div v-for="item in groupSummary.items" :key="item.id" class="flex justify-between">
            <span>
              {{ item.type === 'course' ? 'Kurs' : 'Produkt' }}
              <template v-if="item.type === 'product' && item.itemData?.name">— {{ item.itemData.name }}</template>
            </span>
            <span class="font-medium">{{ formatCurrency(item.total_amount) }}</span>
          </div>
          <div class="border-t pt-2 mt-2 flex justify-between text-base font-semibold">
            <span>Gesamtsumme</span>
            <span class="text-mifo-primary">{{ formatCurrency(groupSummary.totalCents) }}</span>
          </div>
        </div>
      </div>

      <!-- Edit Customer Modal -->
      <div v-if="showEditModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6">
          <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">Kundendaten bearbeiten</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input v-model="editForm.anrede" class="input-field" placeholder="Anrede" />
            <input v-model="editForm.titel" class="input-field" placeholder="Titel" />
            <input v-model="editForm.firstName" class="input-field" placeholder="Vorname" />
            <input v-model="editForm.lastName" class="input-field" placeholder="Nachname" />
            <input v-model="editForm.email" class="input-field md:col-span-2" placeholder="E-Mail" />
            <input v-model="editForm.phone" class="input-field md:col-span-2" placeholder="Telefon" />
            <input v-model="editForm.street" class="input-field" placeholder="Straße" />
            <input v-model="editForm.houseNumber" class="input-field" placeholder="Hausnummer" />
            <input v-model="editForm.postalCode" class="input-field" placeholder="PLZ" />
            <input v-model="editForm.city" class="input-field" placeholder="Ort" />
            <input v-model="editForm.country" class="input-field md:col-span-2" placeholder="Land (Code)" />
          </div>
          <div class="flex justify-end space-x-3 mt-6">
            <button class="btn-outline" @click="showEditModal = false">Abbrechen</button>
            <button class="btn-secondary" @click="saveCustomer(false)">Speichern</button>
            <button class="btn-primary" @click="saveCustomer(true)">Speichern + Stornorechnung & neue Rechnung</button>
          </div>
        </div>
      </div>

      <!-- Refund Modal -->
      <div v-if="showRefundModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">Rückerstattung</h3>

          <div class="space-y-3 mb-4 text-sm">
            <div class="flex justify-between"><span class="text-gray-500">Originalbetrag</span><span class="font-medium">{{ formatCurrency(refundSummary.originalAmount) }}</span></div>
            <div class="flex justify-between"><span class="text-gray-500">Bereits erstattet</span><span class="font-medium">{{ formatCurrency(refundSummary.refundedAmount) }}</span></div>
            <div class="flex justify-between"><span class="text-gray-500">Verfügbar</span><span class="font-medium">{{ formatCurrency(refundSummary.refundableRemaining) }}</span></div>
          </div>

          <div class="space-y-3">
            <label class="block text-sm font-medium text-gray-700">Betrag (in €)</label>
            <input type="number" step="0.01" min="0" v-model.number="refundForm.amountEuro" class="input-field w-full" :max="(refundSummary.refundableRemaining/100).toFixed(2)" />
            <label class="block text-sm font-medium text-gray-700">Grund (optional)</label>
            <input type="text" v-model="refundForm.reason" class="input-field w-full" placeholder="Begründung" />
          </div>

          <div class="flex justify-end space-x-3 mt-6">
            <button class="btn-outline" @click="closeRefundDialog">Abbrechen</button>
            <button class="btn-primary" :disabled="isRefunding || refundSummary.refundableRemaining <= 0 || !isValidRefundAmount" @click="submitRefund">
              <span v-if="isRefunding" class="animate-spin">⏳</span>
              <span v-else>Erstatten</span>
            </button>
          </div>
        </div>
      </div>
      <!-- Status Change Modal -->
      <div v-if="showStatusModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">Status ändern</h3>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Aktueller Status</label>
              <div class="flex items-center space-x-2">
                <StatusPill :value="order.status" />
                <span class="text-sm text-gray-500">{{ getStatusDescription(order.status) }}</span>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Neuer Status *</label>
              <select v-model="statusChangeForm.newStatus" class="input-field w-full" required>
                <option value="">Bitte wählen</option>
                <option value="paid">Bezahlt</option>
                <option value="cancelled">Storniert</option>
              </select>
              <p class="text-sm text-gray-500 mt-1">
                <strong>Hinweis:</strong> Bei Statusänderung zu "Storniert" wird der Kursplatz automatisch freigegeben.
              </p>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Grund (optional)</label>
              <textarea 
                v-model="statusChangeForm.reason" 
                class="input-field w-full" 
                rows="3"
                placeholder="Grund für die Statusänderung..."
              ></textarea>
            </div>
          </div>

          <div class="flex justify-end space-x-3 mt-6">
            <button class="btn-outline" @click="showStatusModal = false">Abbrechen</button>
            <button 
              class="btn-primary" 
              :disabled="!statusChangeForm.newStatus || changingStatus"
              @click="changeOrderStatus"
            >
              <span v-if="changingStatus" class="animate-spin">⏳</span>
              <span v-else>Status ändern</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Billing Address -->
      <div class="card" v-if="order.billing">
        <h3 class="text-lg font-heading font-semibold text-mifo-primary mb-4">
          Abweichende Rechnungsadresse
        </h3>
        <div class="space-y-3">
          <div>
            <span class="text-sm font-medium text-gray-500">Name:</span>
            <p class="text-sm text-gray-900">
              {{ order.billing.firstName }} {{ order.billing.lastName }}
            </p>
          </div>
          <div v-if="order.billing.street">
            <span class="text-sm font-medium text-gray-500">Adresse:</span>
            <p class="text-sm text-gray-900">
              {{ order.billing.street }} {{ order.billing.houseNumber }}<br>
              {{ order.billing.postalCode }} {{ order.billing.city }}<br>
              {{ getCountryName(order.billing.country) }}
            </p>
          </div>
        </div>
      </div>



      <!-- Invoice Section -->
      <div class="card">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-heading font-semibold text-mifo-primary">
            Rechnungen & Stornorechnungen
          </h3>
          <button
            @click="generateInvoice"
            class="btn-primary"
            :disabled="generatingInvoice"
          >
            <span v-if="generatingInvoice" class="animate-spin">⏳</span>
            <span v-else>Neue Rechnung generieren</span>
          </button>
        </div>
        
        <div v-if="invoices.length === 0" class="text-center py-8 text-gray-500">
          <p>Keine Rechnungen vorhanden</p>
        </div>
        
        <div v-else class="space-y-4">
          <div 
            v-for="invoice in invoices" 
            :key="invoice.id"
            class="border rounded-lg p-4"
            :class="[
              invoice.reason === 'credit_note' 
                ? 'bg-red-50 border-red-200' 
                : 'bg-gray-50 border-gray-200'
            ]"
          >
            <div class="flex justify-between items-center">
              <div class="flex-1">
                <div class="flex items-center space-x-3 mb-2">
                  <h4 class="font-semibold" :class="[
                    invoice.reason === 'credit_note' ? 'text-red-900' : 'text-gray-900'
                  ]">
                    {{ invoice.invoiceNumber }}
                  </h4>
                  <StatusPill :value="invoice.reason === 'credit_note' ? 'cancelled' : 'paid'" />
                  <span 
                    v-if="invoice.reason === 'credit_note'"
                    class="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full"
                  >
                    <RotateCcw class="h-3 w-3 mr-1" />
                    Stornorechnung
                  </span>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p class="text-gray-900">{{ getInvoiceReasonName(invoice.reason) }}</p>
                  </div>
                  <div>
                    <p class="text-gray-900">{{ formatDate(invoice.createdAt) }}</p>
                  </div>
                </div>
              </div>
              
              <div class="flex space-x-2">
                <button
                  @click="downloadInvoice(invoice)"
                  class="btn-outline btn-sm inline-flex items-center"
                  :disabled="!invoice.pdfUrl"
                  :class="[
                    invoice.reason === 'credit_note' 
                      ? 'border-red-300 text-red-700 hover:!bg-red-50 hover:!border-red-400 hover:!text-red-800' 
                      : ''
                  ]"
                >
                  <Download class="h-4 w-4 mr-2" />
                  {{ invoice.reason === 'credit_note' ? 'Stornorechnung herunterladen' : 'ZUGFeRD herunterladen' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <h2 class="text-2xl font-heading font-bold text-gray-900 mb-4">Bestellung nicht gefunden</h2>
        <p class="text-gray-600 mb-6">Die angeforderte Bestellung konnte nicht geladen werden.</p>
        <router-link 
          to="/herzbeben/courses" 
          class="btn-primary"
        >
          ← Zurück zu den Kursen
        </router-link>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, onMounted, computed, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AdminLayout from '@/components/Layout/AdminLayout.vue'
import { Pencil, Download, RotateCcw, Clock, Settings } from 'lucide-vue-next'
import { formatCurrency, formatDate } from 'shared/utils'
import api from '@/utils/axios'
import StatusPill from '@/components/StatusPill.vue'

export default {
  name: 'OrderDetail',
  components: {
    AdminLayout,
    Pencil,
    Download,
    RotateCcw,
    Clock,
    StatusPill,
    Settings
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const currentOrderId = computed(() => route.params.orderId || route.params.id)
    const order = ref(null)
    const groupSummary = ref(null)
    const course = ref(null)
    const invoices = ref([])
    const loading = ref(true)
    const generatingInvoice = ref(false)
    const showRefundModal = ref(false)
    const isRefunding = ref(false)
    const refundSummary = reactive({ refunds: [], refundedAmount: 0, refundableRemaining: 0, originalAmount: 0 })
    const refundForm = reactive({ amountEuro: 0, reason: '' })
    const showEditModal = ref(false)
    const showStatusModal = ref(false)
    const changingStatus = ref(false)
    const editForm = reactive({
      anrede: '',
      titel: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: ''
    })

    const statusChangeForm = reactive({
      newStatus: '',
      reason: ''
    })

    const fetchOrderData = async () => {
      loading.value = true
      try {
        const orderId = currentOrderId.value
        const orderResponse = await api.get(`/orders/admin/${orderId}`)
        order.value = orderResponse.data
        
        // Fetch course details if available
        if (order.value.course_id) {
          try {
            const courseResponse = await api.get(`/courses/admin/${order.value.course_id}`)
            course.value = courseResponse.data
          } catch (error) {
            console.error('Error fetching course:', error)
          }
        }
        // Initialize edit form from order data
        if (order.value?.customer) {
          Object.assign(editForm, {
            anrede: order.value.customer.anrede || '',
            titel: order.value.customer.titel || '',
            firstName: order.value.customer.firstName || '',
            lastName: order.value.customer.lastName || '',
            email: order.value.customer.email || '',
            phone: order.value.customer.phone || '',
            street: order.value.customer.street || '',
            houseNumber: order.value.customer.houseNumber || '',
            postalCode: order.value.customer.postalCode || '',
            city: order.value.customer.city || '',
            country: order.value.customer.country || ''
          })
        }
        // Fetch grouped summary if available
        try {
          const grp = await api.get(`/orders/${orderId}/group`)
          groupSummary.value = grp.data
        } catch (e) {
          groupSummary.value = null
        }
      } catch (error) {
        console.error('Error fetching order data:', error)
        order.value = null
      } finally {
        loading.value = false
      }
    }

    const editOrder = () => {
      showEditModal.value = true
    }

    const saveCustomer = async (reissue) => {
      try {
        const payload = { customer: { ...editForm }, reissueInvoices: !!reissue }
        if (reissue) {
          payload.cancellationReason = 'Kundendaten geändert'
        }
        const resp = await api.put(`/orders/${route.params.id}/customer`, payload)
        if (resp.data?.order) {
          order.value = resp.data.order
        }
        if (resp.data?.invoices) {
          invoices.value = resp.data.invoices
        }
        showEditModal.value = false
      } catch (error) {
        console.error('Error saving customer:', error)
        alert('Fehler beim Speichern der Kundendaten')
      }
    }

    const openRefundDialog = async () => {
      try {
        await fetchRefundSummary()
        refundForm.amountEuro = Number((refundSummary.refundableRemaining / 100).toFixed(2))
        showRefundModal.value = true
      } catch (e) {
        console.error('Error opening refund dialog:', e)
      }
    }

    const closeRefundDialog = () => {
      showRefundModal.value = false
    }

    const isValidRefundAmount = computed(() => {
      if (!refundSummary) return false
      const cents = Math.round((refundForm.amountEuro || 0) * 100)
      return cents > 0 && cents <= refundSummary.refundableRemaining
    })

    const fetchRefundSummary = async () => {
      const resp = await api.get(`/orders/${currentOrderId.value}/refunds`)
      Object.assign(refundSummary, resp.data || {})
    }

    const submitRefund = async () => {
      if (!isValidRefundAmount.value) return
      isRefunding.value = true
      try {
        const cents = Math.round((refundForm.amountEuro || 0) * 100)
        await api.post(`/orders/${currentOrderId.value}/refund`, { amount: cents, reason: refundForm.reason })
        await fetchOrderData()
        await fetchInvoices()
        await fetchRefundSummary()
        showRefundModal.value = false
      } catch (error) {
        console.error('Error refunding order:', error)
        alert('Fehler bei der Rückerstattung')
      } finally {
        isRefunding.value = false
      }
    }

    const getCountryName = (code) => {
      const countries = {
        'DE': 'Deutschland',
        'AT': 'Österreich',
        'CH': 'Schweiz',
        'US': 'Vereinigte Staaten',
        'GB': 'Großbritannien'
      }
      return countries[code] || code
    }



    const getPaymentMethodName = (method) => {
      const methods = {
        'creditcard': 'Kreditkarte',
        'paypal': 'PayPal',
        'banktransfer': 'Banküberweisung',
        'paybybank': 'Pay by Bank'
      }
      return methods[method] || method
    }

    const getCourseTypeName = (type) => {
      const types = {
        'standard': 'Standard',
        'assistenzarzt': 'Assistenzarzt'
      }
      return types[type] || type
    }

    // Static time remaining calculation
    const timeUntilExpiration = computed(() => {
      if (!order.value || order.value.status !== 'open' || !order.value.created_at) {
        return null
      }

      // Parse the created_at date correctly (it's stored in UTC)
      const createdTime = new Date(order.value.created_at + 'Z') // Add Z to treat as UTC
      const now = new Date()
      const expirationTime = new Date(createdTime.getTime() + 15 * 60 * 1000) // 15 minutes
      const timeLeft = expirationTime.getTime() - now.getTime()



      if (timeLeft <= 0) {
        return { expired: true, minutes: 0 }
      }

      const minutes = Math.floor(timeLeft / (1000 * 60))
      return { expired: false, minutes }
    })

    const formattedTimeLeft = computed(() => {
      if (!timeUntilExpiration.value) return ''
      
      if (timeUntilExpiration.value.expired) {
        return 'Abgelaufen'
      }

      const { minutes } = timeUntilExpiration.value
      return `${minutes} Min.`
    })

    // Credit note related computed properties
    const hasCreditNotes = computed(() => {
      return invoices.value.some(invoice => invoice.reason === 'credit_note')
    })

    const creditNotesCount = computed(() => {
      return invoices.value.filter(invoice => invoice.reason === 'credit_note').length
    })



    // Invoice-related methods
    const fetchInvoices = async () => {
      try {
        const response = await api.get(`/invoices/orders/${currentOrderId.value}/invoices`)
        invoices.value = response.data.data || []
      } catch (error) {
        console.error('Error fetching invoices:', error)
        invoices.value = []
      }
    }

    const generateInvoice = async () => {
      generatingInvoice.value = true
      try {
        await api.post(`/invoices/orders/${currentOrderId.value}/generate`, {
          reason: 'initial'
        })
        await fetchInvoices()
      } catch (error) {
        console.error('Error generating invoice:', error)
        alert('Fehler beim Generieren der Rechnung')
      } finally {
        generatingInvoice.value = false
      }
    }

    const downloadInvoice = async (invoice) => {
      try {
        const response = await api.get(`/invoices/${invoice.id}/download`, {
          responseType: 'blob'
        })
        
        // Create a temporary link to download the file
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = invoice.pdfFilename || `${invoice.invoiceNumber}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error downloading invoice:', error)
        alert('Fehler beim Herunterladen der Rechnung')
      }
    }





    const getFormattedCustomerName = (customer) => {
      if (!customer) return ''
      const parts = []
      
      // Add salutation (anrede) if available
      if (customer.anrede) {
        parts.push(customer.anrede)
      }
      
      // Add title (titel) if available
      if (customer.titel) {
        parts.push(customer.titel)
      }
      
      // Add first and last name
      if (customer.firstName) {
        parts.push(customer.firstName)
      }
      if (customer.lastName) {
        parts.push(customer.lastName)
      }
      
      return parts.join(' ')
    }

    const getInvoiceReasonName = (reason) => {
      const reasons = {
        'initial': 'Erstausstellung',
        'customer_update': 'Kundendaten geändert',
        'refund': 'Rückerstattung',
        'correction': 'Korrektur',
        'credit_note': 'Stornierung'
      }
      return reasons[reason] || reason
    }

    const getStatusDescription = (status) => {
      const descriptions = {
        'open': 'Zahlung ausstehend',
        'paid': 'Zahlung erfolgreich - Kursplatz reserviert',
        'pending': 'Zahlung wird verarbeitet',
        'failed': 'Zahlung fehlgeschlagen',
        'cancelled': 'Bestellung storniert - Kursplatz freigegeben',
        'expired': 'Zahlungsfrist abgelaufen',
        'refunded': 'Betrag erstattet'
      }
      return descriptions[status] || 'Unbekannter Status'
    }

    const changeOrderStatus = async () => {
      if (!statusChangeForm.newStatus) return
      
      changingStatus.value = true
      try {
        const payload = {
          status: statusChangeForm.newStatus,
          reason: statusChangeForm.reason || null
        }
        
        await api.patch(`/orders/${currentOrderId.value}/status`, payload)
        
        // Reset form
        statusChangeForm.newStatus = ''
        statusChangeForm.reason = ''
        
        showStatusModal.value = false
        
        // Refresh order data
        await fetchOrderData()
        
        // Show success message
        alert('Status erfolgreich geändert!')
      } catch (error) {
        console.error('Error changing order status:', error)
        alert('Fehler beim Ändern des Status: ' + (error.response?.data?.error || error.message))
      } finally {
        changingStatus.value = false
      }
    }

    onMounted(() => {
      fetchOrderData()
      fetchInvoices()
      fetchRefundSummary()
    })

    return {
      order,
      course,
      invoices,
      generatingInvoice,
        showRefundModal,
      showEditModal,
      showStatusModal,
      editForm,
      loading,
      formatCurrency,
      formatDate,
      editOrder,
      openRefundDialog,
      closeRefundDialog,
      isRefunding,
      refundForm,
      refundSummary,
      isValidRefundAmount,
      submitRefund,
      generateInvoice,
      downloadInvoice,
      saveCustomer,
      timeUntilExpiration,
      formattedTimeLeft,
      hasCreditNotes,
      creditNotesCount,

      getCountryName,
      getPaymentMethodName,
      getCourseTypeName,
      getInvoiceReasonName,
      getFormattedCustomerName,
      groupSummary,
      statusChangeForm,
      changingStatus,
      changeOrderStatus,
      getStatusDescription
    }
  }
}
</script> 